import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { logger } from '../lib/productionLogger';

interface UseTabVisibilityOptions {
  onVisible?: () => void;
  onHidden?: () => void;
  refreshSessionOnVisible?: boolean;
}

/**
 * Hook to handle tab visibility changes
 * Refreshes auth session when tab becomes visible (single flight)
 * Prevents duplicate bootstrap loops
 */
export function useTabVisibility(options: UseTabVisibilityOptions = {}) {
  const { onVisible, onHidden, refreshSessionOnVisible = true } = options;
  const refreshingRef = useRef(false);
  const lastRefreshRef = useRef<number>(0);

  const MIN_REFRESH_INTERVAL = 5000; // Minimum 5 seconds between refreshes

  const handleVisibilityChange = useCallback(async () => {
    const isVisible = document.visibilityState === 'visible';

    if (isVisible) {
      logger.log('[TabVisibility] Tab became visible');

      // Call custom onVisible callback
      if (onVisible) {
        onVisible();
      }

      // Refresh session if enabled and not already refreshing
      if (refreshSessionOnVisible && !refreshingRef.current) {
        const now = Date.now();
        const timeSinceLastRefresh = now - lastRefreshRef.current;

        // Only refresh if enough time has passed since last refresh
        if (timeSinceLastRefresh < MIN_REFRESH_INTERVAL) {
          logger.log('[TabVisibility] Skipping refresh - too soon');
          return;
        }

        refreshingRef.current = true;
        lastRefreshRef.current = now;

        try {
          logger.log('[TabVisibility] Refreshing session...');

          // Get current session
          const { data: { session }, error } = await supabase.auth.getSession();

          if (error) {
            logger.error('[TabVisibility] Session refresh failed:', error);
            return;
          }

          if (!session) {
            logger.log('[TabVisibility] No session to refresh');
            return;
          }

          // Check if session is near expiry (within 5 minutes)
          const expiresAt = session.expires_at;
          if (expiresAt) {
            const expiresIn = expiresAt - Math.floor(Date.now() / 1000);
            const FIVE_MINUTES = 5 * 60;

            if (expiresIn < FIVE_MINUTES) {
              logger.log('[TabVisibility] Session near expiry, forcing refresh');

              // Force refresh the session
              const { error: refreshError } = await supabase.auth.refreshSession();

              if (refreshError) {
                logger.error('[TabVisibility] Failed to refresh session:', refreshError);
              } else {
                logger.log('[TabVisibility] Session refreshed successfully');

                // Trigger a custom event for other parts of the app to react
                window.dispatchEvent(new CustomEvent('auth-session-refreshed'));
              }
            } else {
              logger.log('[TabVisibility] Session valid, no refresh needed');
            }
          }
        } catch (error) {
          logger.error('[TabVisibility] Error during session refresh:', error);
        } finally {
          refreshingRef.current = false;
        }
      }
    } else {
      logger.log('[TabVisibility] Tab became hidden');

      // Call custom onHidden callback
      if (onHidden) {
        onHidden();
      }
    }
  }, [onVisible, onHidden, refreshSessionOnVisible]);

  useEffect(() => {
    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleVisibilityChange]);

  return {
    isVisible: document.visibilityState === 'visible',
    isRefreshing: refreshingRef.current,
  };
}

/**
 * Hook to listen for auth session refresh events
 * Use this in components that need to refetch data when session is refreshed
 */
export function useAuthSessionRefresh(callback: () => void) {
  useEffect(() => {
    const handleRefresh = () => {
      logger.log('[AuthSessionRefresh] Session refreshed, executing callback');
      callback();
    };

    window.addEventListener('auth-session-refreshed', handleRefresh);

    return () => {
      window.removeEventListener('auth-session-refreshed', handleRefresh);
    };
  }, [callback]);
}
